import { FormField, isNone, isSome, Maybe, noop } from '@crocodille/common';
import { Listbox } from '@headlessui/react';
import cn from 'classnames';
import React, { Children, useMemo } from 'react';

import ChevronDownIcon from '../../icons/chevron-down-icon';
import styles from './select-2.module.scss';

export type SelectChild = React.ReactElement<SelectItemProps, typeof Item>;

export interface SelectProps {
  readonly value?: Maybe<string | number>;
  onChange?(value: string | number): void;
  onBlur?(): void;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly className?: string;
  readonly buttonClassName?: string;
  readonly name?: string;
}

const SelectElement: React.FC<React.PropsWithChildren<SelectProps>> = ({
  children,
  value,
  onChange = noop,
  onBlur = noop,
  disabled,
  invalid,
  className,
  buttonClassName,
  name,
}) => {
  const selectedItem = useMemo(
    () =>
      isNone(value)
        ? null
        : (Children.toArray(children) as SelectChild[]).find(
            (child) => child?.props?.value === value,
          ),
    [children, value],
  );

  return (
    <Listbox
      as="div"
      className={cn(
        styles.container,
        { [styles.isInvalid]: invalid },
        className,
      )}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      name={name}
    >
      {({ open }) => (
        <>
          <Listbox.Button
            className={cn(
              styles.button,
              { [styles.isOpen]: open },
              buttonClassName,
            )}
          >
            {selectedItem?.props?.label}
            <div className={styles.dropIconContainer}>
              <ChevronDownIcon className={styles.dropIcon} />
            </div>
          </Listbox.Button>
          <Listbox.Options className={styles.items}>{children}</Listbox.Options>
        </>
      )}
    </Listbox>
  );
};

export interface SelectItemProps {
  readonly value: string | number;
  readonly label: string;
  readonly disabled?: boolean;
}

const Item: React.FC<React.PropsWithChildren<SelectItemProps>> = ({
  value,
  label,
  disabled,
}) => {
  return (
    <Listbox.Option
      as={React.Fragment}
      key={value.toString()}
      value={value}
      disabled={disabled}
    >
      {({ active, selected, disabled }) => (
        <li
          className={cn(styles.item, {
            [styles.isActive]: active,
            [styles.isSelected]: selected,
            [styles.isDisabled]: disabled,
          })}
        >
          {label === '' ? <>&nbsp;</> : label}
        </li>
      )}
    </Listbox.Option>
  );
};

const Select = Object.assign(SelectElement, { Item });

export function fieldToSelectProps({
  value,
  onChange,
  onBlur,
  disabled,
  error,
}: FormField<string | number | null>): Pick<
  SelectProps,
  'value' | 'onChange' | 'onBlur' | 'invalid' | 'disabled'
> {
  return {
    value: value ?? '',
    onChange,
    onBlur,
    disabled,
    invalid: isSome(error),
  };
}

export default Select;
